import type { KvkMutatieVeld, KvkRegistratieStatus } from '@streep/types'

/**
 * batch-46 relaties-kvk (FO-395) — pure diff-berekening voor de KVK-mutatiescan.
 *
 * Neemt een genormaliseerde KVK-payload + een Client-snapshot en levert de
 * voorstellen (nooit een DB-write, nooit een auto-apply). Volledig unit-testbaar:
 * de cron mockt de KVK-client en voedt deze functie; de DB-effecten (upsert
 * voorstel, badge schrijven, signaal vuren) leven in de cron.
 *
 * Vergeleken velden (spec §3.2): statutaire naam, handelsnaam, vestigingsadres
 * en registratiestatus (actief / opgeheven / faillissement / surseance).
 */

/** Genormaliseerde KVK-momentopname (door de KVK-client geleverd). */
export interface KvkSnapshot {
  statutaireNaam: string | null
  handelsnaam: string | null
  straat: string | null
  huisnummer: string | null
  postcode: string | null
  plaats: string | null
  registratieStatus: KvkRegistratieStatus
}

/** Relevante Client-velden voor de diff. */
export interface ClientKvkSnapshot {
  name: string
  street: string | null
  houseNumber: string | null
  postalCode: string | null
  city: string | null
  /** Laatst vastgelegde KVK-status; `null` = nog nooit gescand (baseline = ACTIEF). */
  kvkRegistratieStatus: KvkRegistratieStatus | null
  /** Aantal actieve (niet-verwijderde) locaties — bepaalt adres-ambiguïteit. */
  actieveLocatieCount: number
}

/** Eén gedetecteerde afwijking (→ één KvkMutatieVoorstel). */
export interface KvkVoorstelDiff {
  veld: KvkMutatieVeld
  oudeWaarde: string | null
  /** Dedup-sleutel (uniek per clientId+veld+nieuweWaarde); voor status = de code. */
  nieuweWaarde: string
  nieuwStraat: string | null
  nieuwHuisnummer: string | null
  nieuwPostcode: string | null
  nieuwPlaats: string | null
  /** Alleen bij ADRES + meerdere locaties: een locatie-keuze is verplicht (geen gok). */
  ambigueLocatie: boolean
  /** Alleen bij REGISTRATIESTATUS gezet. */
  nieuweStatus: KvkRegistratieStatus | null
}

export interface KvkDiffResultaat {
  voorstellen: KvkVoorstelDiff[]
  /** Statuswijziging t.o.v. de laatst vastgelegde KVK-status (null = geen wijziging). */
  statusWijziging: { van: KvkRegistratieStatus; naar: KvkRegistratieStatus } | null
  /** True zodra de nieuwe status opgeheven/faillissement/surseance is. */
  isRisicoStatus: boolean
}

/** Case-/spatie-ongevoelige normalisatie voor de gelijkheids-check. */
function normaliseer(waarde: string | null | undefined): string {
  return (waarde ?? '').trim().replace(/\s+/g, ' ').toLowerCase()
}

/** Canonieke adres-weergave; leeg als er geen zinvol adres is. */
export function formatteerAdres(
  straat: string | null,
  huisnummer: string | null,
  postcode: string | null,
  plaats: string | null,
): string {
  const regel1 = [straat ?? '', huisnummer ?? ''].join(' ').trim()
  const regel2 = [postcode ?? '', plaats ?? ''].join(' ').trim()
  return [regel1, regel2].filter((s) => s.length > 0).join(', ')
}

const RISICO_STATUSSEN: ReadonlySet<KvkRegistratieStatus> = new Set([
  'OPGEHEVEN',
  'FAILLISSEMENT',
  'SURSEANCE',
])

/**
 * Bereken de voorstellen. Geen enkele bijwerking — de cron beslist wat ermee
 * gebeurt (upsert, badge-write bij status, signaal).
 */
export function berekenKvkVoorstellen(
  kvk: KvkSnapshot,
  client: ClientKvkSnapshot,
): KvkDiffResultaat {
  const voorstellen: KvkVoorstelDiff[] = []

  const clientNaamNorm = normaliseer(client.name)
  const statutaireNorm = normaliseer(kvk.statutaireNaam)
  const handelsnaamNorm = normaliseer(kvk.handelsnaam)

  // ── Naam ────────────────────────────────────────────────────────────────────
  // De klantnaam is prima zolang hij één van de KVK-namen (statutaire OF
  // handelsnaam) matcht — dan geen ruis. Matcht hij géén van beide, dan is de
  // naam mogelijk verouderd: bied de statutaire naam en (indien afwijkend) de
  // handelsnaam als voorstel(len) aan, zodat de beoordelaar kan kiezen.
  const naamMatchtKvk =
    (kvk.statutaireNaam && clientNaamNorm === statutaireNorm) ||
    (kvk.handelsnaam && clientNaamNorm === handelsnaamNorm)

  if (!naamMatchtKvk) {
    if (kvk.statutaireNaam && statutaireNorm !== clientNaamNorm) {
      voorstellen.push({
        veld: 'NAAM',
        oudeWaarde: client.name,
        nieuweWaarde: kvk.statutaireNaam.trim(),
        nieuwStraat: null,
        nieuwHuisnummer: null,
        nieuwPostcode: null,
        nieuwPlaats: null,
        ambigueLocatie: false,
        nieuweStatus: null,
      })
    }
    // Handelsnaam alleen als hij afwijkt van zowel de klantnaam als de statutaire
    // naam (anders een duplicaat-voorstel).
    if (
      kvk.handelsnaam &&
      handelsnaamNorm !== clientNaamNorm &&
      handelsnaamNorm !== statutaireNorm
    ) {
      voorstellen.push({
        veld: 'HANDELSNAAM',
        oudeWaarde: client.name,
        nieuweWaarde: kvk.handelsnaam.trim(),
        nieuwStraat: null,
        nieuwHuisnummer: null,
        nieuwPostcode: null,
        nieuwPlaats: null,
        ambigueLocatie: false,
        nieuweStatus: null,
      })
    }
  }

  // ── Vestigingsadres (alleen bij voldoende KVK-adresdata) ────────────────────
  if (kvk.straat && kvk.plaats) {
    const oudAdres = formatteerAdres(client.street, client.houseNumber, client.postalCode, client.city)
    const nieuwAdres = formatteerAdres(kvk.straat, kvk.huisnummer, kvk.postcode, kvk.plaats)
    if (normaliseer(oudAdres) !== normaliseer(nieuwAdres)) {
      voorstellen.push({
        veld: 'ADRES',
        oudeWaarde: oudAdres.length > 0 ? oudAdres : null,
        nieuweWaarde: nieuwAdres,
        nieuwStraat: kvk.straat,
        nieuwHuisnummer: kvk.huisnummer,
        nieuwPostcode: kvk.postcode,
        nieuwPlaats: kvk.plaats,
        // Meerdere locaties → mapping ambigu → locatie-keuze verplicht (T6).
        ambigueLocatie: client.actieveLocatieCount > 1,
        nieuweStatus: null,
      })
    }
  }

  // ── Registratiestatus ───────────────────────────────────────────────────────
  const baseline: KvkRegistratieStatus = client.kvkRegistratieStatus ?? 'ACTIEF'
  let statusWijziging: KvkDiffResultaat['statusWijziging'] = null
  if (kvk.registratieStatus !== baseline) {
    statusWijziging = { van: baseline, naar: kvk.registratieStatus }
    voorstellen.push({
      veld: 'REGISTRATIESTATUS',
      oudeWaarde: baseline,
      nieuweWaarde: kvk.registratieStatus,
      nieuwStraat: null,
      nieuwHuisnummer: null,
      nieuwPostcode: null,
      nieuwPlaats: null,
      ambigueLocatie: false,
      nieuweStatus: kvk.registratieStatus,
    })
  }

  return {
    voorstellen,
    statusWijziging,
    isRisicoStatus: RISICO_STATUSSEN.has(kvk.registratieStatus),
  }
}

/**
 * Deterministische spreiding: elke relatie heeft een vast scanmoment binnen de
 * 28-daagse cyclus, zodat de belasting op de KVK-API vlak blijft en elke relatie
 * ~maandelijks geraakt wordt (spec §3.1 / §8.2).
 *
 * `hash(clientId) % 28 === dagVanDeMaand % 28` — pure, stabiel per relatie.
 */
export function isScanDagVoorClient(clientId: string, dagVanDeMaand: number, deler = 28): boolean {
  return hashString(clientId) % deler === dagVanDeMaand % deler
}

/** Stabiele, niet-cryptografische string-hash (djb2-variant), altijd ≥ 0. */
export function hashString(value: string): number {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i)
  }
  // >>> 0 → unsigned 32-bit; zo is % deler altijd niet-negatief.
  return hash >>> 0
}
